import { isPlainObject, isString } from 'lodash';

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';
export type ParameterType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'enum';

// Either a string or an array of lines to allow minimal formatting.
export type Text = string | string[];
export type TextIntl = { en: Text } & Record<string, null | Text>;
export function isTextIntl(val: unknown): val is TextIntl {
    return (
        isPlainObject(val) &&
        Object.hasOwnProperty.call(val, 'en') &&
        Object.values(val as object).every(v => (Array.isArray(v) && v.every(isString)) || isString(v))
    );
}

export interface ParamBase<TypeEnum extends ParameterType, Type = unknown> {
    description?: TextIntl;
    type: TypeEnum;

    required?: boolean;
    null?: boolean;

    default?: null | Type | TextIntl;
    example?: Type;
}

export type ParamString = ParamBase<'string', string>;
export type ParamNumber = ParamBase<'number', number> & { min?: number; max?: number };
export type ParamBoolean = ParamBase<'boolean', boolean>;
export type ParamObject = ParamBase<'object', Dict> & { properties: Record<string, ParamAny> };
export type ParamArray = ParamBase<'array', unknown[]> & { entry: ParamAny };
export type ParamEnum = ParamBase<'enum', string | number> & { options: Array<string | number> };

export type ParamAny = ParamString | ParamNumber | ParamBoolean | ParamObject | ParamArray | ParamEnum;

export type EndpointInput = ParamObject | ParamArray;
export type EndpointOutput = ParamObject | ParamArray;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type FunctionPropertyNames<T> = { [K in keyof T]: T[K] extends Function ? K : never }[keyof T];
type Plain<T extends Dict> = Omit<T, FunctionPropertyNames<T>>;

export class Endpoint {
    public title: TextIntl;
    public description?: TextIntl;

    public url: string;
    public methods: Method[];

    public input?: EndpointInput;
    public output: EndpointOutput;

    constructor(data: Plain<Endpoint>) {
        this.title = data.title;
        this.description = data.description;

        this.url = data.url;
        this.methods = data.methods;

        this.input = data.input;
        this.output = data.output;
    }
    static getText(entry: TextIntl, lang: string): string {
        // Select the language, defaulting to English.
        const text: Text = entry[lang] || entry.en;

        // Join the lines if it's an array.
        return Array.isArray(text) ? text.join('\n') : text;
    }
}
